import { Controller } from "@hotwired/stimulus"

// Polls the project_status endpoint on a project detail page and
// auto-reloads when anything meaningful lands: research completes,
// MW gets confirmed, or the forecast finishes computing. Replaces
// the "refresh in a minute" copy with a live indicator — same shape
// as gs-discovery-monitor but tracking project-level state instead
// of jurisdiction-level project counts.
//
// Markup:
//   <div data-controller="gs-project-monitor"
//        data-gs-project-monitor-url-value="/gridsignal/projects/4/project_status"
//        data-gs-project-monitor-initial-researched-value="false"
//        data-gs-project-monitor-initial-mw-value="false"
//        data-gs-project-monitor-initial-forecast-value="false"
//        data-gs-project-monitor-initial-sources-value="0">
//     <span data-gs-project-monitor-target="status">Still researching…</span>
//     <span data-gs-project-monitor-target="elapsed">0s</span>
//   </div>
export default class extends Controller {
  static targets = ["status", "elapsed"]
  static values = {
    url:              String,
    initialResearched:{ type: Boolean, default: false },
    initialMw:        { type: Boolean, default: false },
    initialForecast:  { type: Boolean, default: false },
    initialSources:   { type: Number,  default: 0 },
    pollInterval:     { type: Number,  default: 6000 },
    maxDurationMs:    { type: Number,  default: 180000 }
  }

  connect() {
    this.startedAt = Date.now()
    this.schedule()
    this.tickElapsed()
    this.elapsedTimer = setInterval(() => this.tickElapsed(), 1000)
  }

  disconnect() {
    clearTimeout(this.pollTimer)
    clearInterval(this.elapsedTimer)
  }

  schedule() {
    this.pollTimer = setTimeout(() => this.poll(), this.pollIntervalValue)
  }

  async poll() {
    if (Date.now() - this.startedAt > this.maxDurationMsValue) {
      this.stopWithMessage("Still looking — this market may need more time. Come back soon.")
      return
    }

    try {
      const resp = await fetch(this.urlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()

      const nowResearched = !!data.researched_at
      const nowMw         = !!data.has_power_mw
      const nowForecast   = !!data.has_forecast
      const nowSources    = Number(data.research_sources_count || 0)

      const changed =
        (!this.initialResearchedValue && nowResearched) ||
        (!this.initialMwValue         && nowMw)         ||
        (!this.initialForecastValue   && nowForecast)   ||
        (nowSources > this.initialSourcesValue)

      if (changed) {
        this.stopWithMessage("New details landed — loading…")
        setTimeout(() => window.location.reload(), 400)
        return
      }
    } catch (err) {
      // Transient failures — keep polling.
    }

    this.schedule()
  }

  stopWithMessage(message) {
    clearTimeout(this.pollTimer)
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  tickElapsed() {
    if (!this.hasElapsedTarget) return
    const seconds = Math.floor((Date.now() - this.startedAt) / 1000)
    this.elapsedTarget.textContent = `${seconds}s`
  }
}
